import { Link, useParams } from 'react-router-dom'
import PageHeader from '../../components/layout/PageHeader'
import useDeccaConversation from '../../hooks/decca/useDeccaConversation'
import formatConversationDate from '../../helpers/decca/formatConversationDate'
import sanitizeHtml, { DEFAULT_ALLOWED_TAGS } from '../../utils/sanitizeHtml'
import { client } from '../../hooks/Client'

// AI-CONTEXT: See docs/features/decca-gesprekken-overzicht.md

// Decca's systeemprompt vraagt om <h2>/<h3> in de antwoorden; de standaardlijst
// haalt die weg en laat de koppen als kale tekst achter. Zelfde aanvulling als
// in het chatvenster zelf (src/components/AI/decca/Decca.jsx).
const DECCA_ALLOWED_TAGS = [...DEFAULT_ALLOWED_TAGS, 'h2', 'h3']

/**
 * Eén volledig gesprek met Decca, zoals de organisatie het voerde.
 *
 * De berichttekst is HTML uit de database en wordt door `sanitizeHtml` gehaald
 * voordat hij in de pagina komt.
 *
 * Drie toestanden, in deze volgorde: nog niet bekend, mislukt, en pas dan de
 * inhoud. Een gesprek zónder berichten bestaat echt — 12 van de 337 gesprekken
 * in productie hebben er geen — en dat is iets anders dan een gesprek dat nog
 * aan het laden is.
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 */
const DeccaConversationDetail = () => {

    const { conversationId } = useParams()
    const { conversation, messages, loading, error } = useDeccaConversation(conversationId)

    return (
        <div className='decca-conversation'>
            <PageHeader heading={conversation?.title || 'Gesprek met Decca'} />

            <p className='decca-conversation__back'>
                <Link to={`/${client}/deccagesprekken`}>Terug naar alle gesprekken</Link>
            </p>

            {error ? (
                <p className='decca-conversation__state decca-conversation__state--error'>{error}</p>
            ) : loading || !conversation ? (
                <p className='decca-conversation__state'>Bezig met laden…</p>
            ) : (
                <>
                    <p className='decca-conversation__meta'>
                        {`${conversation.communityName || 'Onbekende organisatie'} · ${formatConversationDate(conversation.timestampMs)}`}
                    </p>

                    {!messages || messages.length === 0 ? (
                        <p className='decca-conversation__state'>
                            Dit gesprek bevat geen berichten.
                        </p>
                    ) : (
                        <ol className='decca-conversation__messages'>
                            {messages.map((message) => (
                                <li
                                    key={message.id}
                                    className={`decca-conversation__message decca-conversation__message--${message.role || 'unknown'}`}
                                >
                                    <p className='decca-conversation__author'>
                                        {message.role === 'assistant'
                                            ? 'Decca'
                                            : message.userName || (message.role === 'user' ? 'Gebruiker' : 'Onbekend')}
                                    </p>
                                    <div
                                        className='decca-conversation__text'
                                        dangerouslySetInnerHTML={{
                                            __html: sanitizeHtml(message.message, { allowedTags: DECCA_ALLOWED_TAGS }),
                                        }}
                                    />
                                </li>
                            ))}
                        </ol>
                    )}
                </>
            )}
        </div>
    )
}

export default DeccaConversationDetail
